import type { RootState } from 'reducers/types';
import type { Dispatch } from 'redux';
import { getErrors, getPaymentStatus } from 'selectors';
import type { Tracker } from 'services/tracker/tracker';
import type { PayexThunkAction } from 'store/configureStore';

type Services = {
  tracker: Tracker;
};

export const trackEvent = (
  tracker: Tracker,
  name: string,
  ownProps: Record<string, unknown>,
) => {
  const action = 'visit';
  const category = 'visit';
  const label = null;
  const value = null;
  const platform = 'embedded 2.0';
  const defaultProps = {
    action,
    category,
    label,
    value,
    platform,
  };

  const props = { ...defaultProps, ...ownProps };

  return tracker.trackEvent(name, props);
};

export const trackDocCollectorFileUpload =
  (apiResponse: any) =>
  (_dispatch: Dispatch, getState: () => RootState, { tracker }: Services) => {
    const name = 'Upload';
    const action = 'upload';
    const category = 'document collection';
    const label = getPaymentStatus(getState());
    const value = apiResponse;

    const props = { action, category, label, value };

    return trackEvent(tracker, name, props);
  };

export const trackDocCollectorFileDownload =
  (value: string) =>
  (_dispatch: Dispatch, getState: () => RootState, { tracker }: Services) => {
    const name = 'visit file';
    const category = 'document collection';
    const action = 'click';
    const label = getPaymentStatus(getState());

    const props = { action, category, label, value };

    return trackEvent(tracker, name, props);
  };

export const trackTermsOfUse =
  (link: string) =>
  (_dispatch: Dispatch, _getState: () => RootState, { tracker }: Services) => {
    const category = 'terms of use';
    const action = 'click';
    const value = link;
    const name = `${action} ${link}`;

    const props = { action, category, value };

    return trackEvent(tracker, name, props);
  };

export const trackFieldsErrors =
  (errorReasons: Record<string, string> = {}) =>
  (_dispatch: Dispatch, getState: () => RootState, { tracker }: Services) => {
    const state = getState();
    const category = 'field';
    const action = 'error';

    return getErrors(state).forEach((field: string) => {
      const name = `field error ${field} ${action}`;

      const props = {
        action,
        category,
        value: errorReasons[field] || 'yes',
        label: field,
      };
      trackEvent(tracker, name, props);
    });
  };

export const trackDownloadInstructions =
  () =>
  (_dispatch, _getState, { tracker }: Services) => {
    const action = 'click';
    const category = 'cta';
    const value = 'download_instructions';

    const props = { category, value, action };
    const name = 'download instructions';

    return trackEvent(tracker, name, props);
  };

export const trackPaymentCompleted =
  () =>
  (_dispatch, _getState, { tracker }: Services) => {
    const action = 'click';
    const category = 'cta';
    const value = 'completed_transfer';

    const props = { category, value, action };
    const name = 'completed transfer';

    return trackEvent(tracker, name, props);
  };

export const trackCancelPayment =
  () =>
  (_dispatch, _getState, { tracker }: Services) => {
    const action = 'click';
    const category = 'cta';
    const value = 'cancel';
    const props = { category, value, action };
    const name = 'cancel payment';

    return trackEvent(tracker, name, props);
  };

export const trackBackButton =
  () =>
  (_dispatch, _getState, { tracker }: Services) => {
    const action = 'click';
    const category = 'cta';
    const value = 'click_on_back';
    const props = { category, value, action };
    const name = 'click on back';

    return trackEvent(tracker, name, props);
  };

export const trackCreateCheckoutPayment =
  () =>
  (_dispatch, _getState, { tracker }: Services) => {
    const action = 'click';
    const category = 'cta';
    const value = 'payment_confirmed';
    const props = { category, value, action };
    const name = 'payment made from embed 2.0';

    return trackEvent(tracker, name, props);
  };
export const trackCreateCheckoutPaymentError =
  (error: any) =>
  (_dispatch, _getState, { tracker }: Services) => {
    const action = 'click';
    const category = 'cta';
    const value = { error };
    const props = { category, value, action };
    const name = 'create a payment from embed 2.0 fails';

    return trackEvent(tracker, name, props);
  };

export const trackLRSSigned =
  ({ method } = {}) =>
  (_dispatch, getState, { tracker }: Services) => {
    const state = getState();
    const { recipient, sender_country: country } = state.ui.fieldValues;

    const category = 'cta';
    const action = 'click option';
    const name = 'LRS signed from embed 2.0';
    const value =
      method === 'CANVAS'
        ? 'save_drawn_signature_lrs_form'
        : 'save_uploaded_signature_lrs_form';

    const props = { category, value, action, recipient, country };

    return trackEvent(tracker, name, props);
  };

export const trackCardElementEventForRecurring =
  ({
    type,
    content,
    sessionId,
  }: {
    type: string;
    content: unknown;
    sessionId: string;
  }): PayexThunkAction =>
  (_, getState, { tracker }: Services) => {
    const state = getState();
    const { recipient, sender_country: country } = state.ui.fieldValues;

    const category = 'cardElement';
    const action = 'event';
    const name = 'card tokenization for instalments embed2.0';

    const props = {
      category,
      action,
      recipient,
      country,
      value: type,
      content,
      checkoutSessionId: sessionId,
    };

    return trackEvent(tracker, name, props);
  };

export const trackSelectInstalmentPlansTab =
  (): PayexThunkAction =>
  (_dispatch, _getState, { tracker }) => {
    const category = 'payment action';
    const action = 'click';
    const label = null;
    const value = 'select_instalment_plans_tab';
    const name = value;
    const props = { action, category, label, value };

    return trackEvent(tracker, name, props);
  };

export const trackSelectSinglePaymentsTab =
  (): PayexThunkAction =>
  (_dispatch, _getState, { tracker }) => {
    const category = 'payment action';
    const action = 'click';
    const label = null;
    const value = 'select_single_payments_tab';
    const name = value;
    const props = { action, category, label, value };
    return trackEvent(tracker, name, props);
  };

export const trackIndiaFxTestAssigned =
  (assigned: boolean): PayexThunkAction =>
  (_dispatch, _getState, { tracker }) => {
    const name = 'india_fx_test_assigned';
    const value = assigned;
    const props = { value };

    return trackEvent(tracker, name, props);
  };
